import type { LeadFormData } from '../types';
import { config } from '../config';

export type LeadResult =
  | { success: true; via: 'endpoint' | 'mailto' }
  | { success: false; error: string };

export interface LeadPayload extends LeadFormData {
  source: string;
  submittedAt: string;
}

/** Data plus the honeypot field. A filled honeypot means a bot submitted the form. */
export type LeadSubmission = LeadFormData & { website?: string };

interface Deps { fetch: (input: string, init: RequestInit) => Promise<Response> }
const defaultDeps: Deps = { fetch: (input, init) => globalThis.fetch(input, init) };

const REQUEST_TIMEOUT_MS = 10_000;

export function buildLeadPayload(data: LeadFormData): LeadPayload {
  const { name, email, org, role, interest, message, assessmentId, assessmentScore, assessmentLevel } = data;
  return {
    name: name.trim(), email: email.trim(), org: org.trim(), role, interest, message: message.trim(),
    ...(assessmentId ? { assessmentId, assessmentScore, assessmentLevel } : {}),
    source: 'empowereddv.com',
    submittedAt: new Date().toISOString(),
  };
}

function buildBody(data: LeadFormData): string {
  const assessment = data.assessmentId
    ? `Assessment Taken: ${data.assessmentId} (${[data.assessmentLevel, data.assessmentScore != null ? `${data.assessmentScore}%` : null].filter(Boolean).join(' · ')})`
    : null;
  return [
    'New lead from EmpowerED website',
    '',
    `Name: ${data.name}`,
    `Email: ${data.email}`,
    data.org ? `Organization: ${data.org}` : null,
    data.role ? `Role: ${data.role}` : null,
    data.interest ? `Interest: ${data.interest}` : null,
    assessment,
    data.message ? `\nMessage:\n${data.message}` : null,
    '',
    '---',
    `Submitted: ${new Date().toISOString()}`,
  ].filter(Boolean).join('\n');
}

export function buildMailtoHref(data: LeadFormData): string {
  const subject = `[EmpowerED Lead] ${data.name}${data.org ? ` — ${data.org}` : ''}`;
  return `mailto:${config.tracyEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildBody(data))}`;
}

function openMailto(data: LeadFormData): LeadResult {
  if (typeof window !== 'undefined') window.location.href = buildMailtoHref(data);
  return { success: true, via: 'mailto' };
}

/**
 * Submit a lead.
 * - Honeypot filled → silently "succeed" without sending anything.
 * - No endpoint configured → open the visitor's mail client (mailto).
 * - Endpoint 2xx → success. 4xx → hard failure shown to the visitor.
 * - Network error / 5xx / timeout → fall back to mailto so the lead is never lost.
 */
export async function sendLead(data: LeadSubmission, deps: Deps = defaultDeps): Promise<LeadResult> {
  const { website, ...lead } = data;
  if (website && website.trim()) return { success: true, via: 'endpoint' };

  const endpoint = config.leadEndpoint;
  if (!endpoint) return openMailto(lead);

  try {
    const res = await deps.fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(buildLeadPayload(lead)),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
    if (res.ok) return { success: true, via: 'endpoint' };
    if (res.status >= 400 && res.status < 500) {
      let error = 'Something went wrong. Please check your details and try again.';
      try { const j = await res.json(); if (j && typeof j.error === 'string') error = j.error; } catch { /* keep default */ }
      return { success: false, error };
    }
    return openMailto(lead);
  } catch {
    return openMailto(lead);
  }
}
